import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Intrest.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  declined: "Declined",
};

const PLACEHOLDER = "https://placehold.co/80x80?text=No+Photo";

function SkeletonRow() {
  return (
    <div className="ir-row ir-skeleton">
      <div className="ir-avatar sk-box" />
      <div className="ir-info">
        <div className="sk-line sk-name" />
        <div className="sk-line sk-sub" />
      </div>
      <div className="ir-meta">
        <div className="sk-line sk-short" />
        <div className="sk-line sk-short" />
      </div>
      <div className="ir-actions">
        <div className="sk-btn" />
        <div className="sk-btn" />
      </div>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`ir-toast ir-toast--${type}`}>
      <i className={`ti ${type === "success" ? "ti-check" : "ti-close"}`} />
      {message}
    </div>
  );
}

function Intrest() {
  const [activeTab, setActiveTab] = useState("received");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [actionPending, setActionPending] = useState({});

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const loadRequests = useCallback(async (tab) => {
    setLoading(true);
    try {
      const res = await VivahApi.Interest.getRequest(tab);
      setRequests(res.data.data || []);
    } catch {
      showToast("Failed to load requests. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadRequests(activeTab);
  }, [activeTab, loadRequests]);

  const handleViewClick = async (id, profileId) => {
    try {
      await VivahApi.Interest.updateInterest(id, { isRead: true });
    } catch {
      // non-critical — navigate anyway
    }
    window.location.href = `/profile/${profileId}`;
  };

  const handleAction = async (id, status) => {
    setActionPending((p) => ({ ...p, [id]: true }));
    try {
      await VivahApi.Interest.updateInterest(id, {
        status,
        isRequestAccept: status === "accepted",
      });
      setRequests((prev) =>
        prev.map((r) => (r.interestId === id ? { ...r, status } : r))
      );
      showToast(
        status === "accepted"
          ? "Request accepted! You can now chat."
          : "Request declined.",
        status === "accepted" ? "success" : "info"
      );
    } catch {
      showToast("Action failed. Please try again.", "error");
    } finally {
      setActionPending((p) => ({ ...p, [id]: false }));
    }
  };

  const unreadCount = requests.filter((r) => !r.isRead).length;

  return (
    <>
      <div className="gla_page" id="gla_page">
        <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
        <Header />

        {/* Page hero — commented out
        <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
          <div className="container text-left">
            <div className="row">
              <div className="col-md-8">
                <h1 className="gla_h1_title">Requests</h1>
              </div>
              <div className="col-md-4">
                <div className="breadcrumbs">
                  <a href="#">Home</a><span>Requests</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        */}

        <section id="gla_content" className="gla_content page-top-offset">
          <section className="gla_section">
            <div className="container">

              {/* Tabs */}
              <div className="ir-tabs">
                <button
                  className={`ir-tab${activeTab === "received" ? " ir-tab--active" : ""}`}
                  onClick={() => setActiveTab("received")}
                >
                  <i className="ti ti-arrow-down" /> Received
                  {activeTab === "received" && unreadCount > 0 && (
                    <span className="ir-tab-badge">{unreadCount}</span>
                  )}
                </button>
                <button
                  className={`ir-tab${activeTab === "sent" ? " ir-tab--active" : ""}`}
                  onClick={() => setActiveTab("sent")}
                >
                  <i className="ti ti-arrow-up" /> Sent
                </button>
              </div>

              {/* Request list */}
              <div className="ir-list">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                ) : requests.length === 0 ? (
                  <div className="ir-empty">
                    <i className="ti ti-heart-broken" />
                    <p>
                      {activeTab === "received"
                        ? "No one has sent you a request yet."
                        : "You haven't sent any requests yet."}
                    </p>
                    {activeTab === "sent" && (
                      <Link className="ir-empty-cta" to="/search">
                        Browse Matches
                      </Link>
                    )}
                  </div>
                ) : (
                  requests.map((e) => {
                    const isPending = !e.status || e.status === "pending";
                    const profileId =
                      activeTab === "received" ? e.fromUserId : e.toUserId;
                    return (
                      <div
                        className={`ir-row${!e.isRead && activeTab === "received" ? " ir-row--unread" : ""}`}
                        key={e.interestId}
                      >
                        {/* Unread dot */}
                        {!e.isRead && activeTab === "received" && (
                          <span className="ir-unread-dot" title="New" />
                        )}

                        {/* Avatar */}
                        <div className="ir-avatar-wrap">
                          <img
                            className="ir-avatar"
                            src={e.otherUser?.photo || PLACEHOLDER}
                            alt={e.otherUser?.name || "User"}
                            onError={(ev) => { ev.target.src = PLACEHOLDER; }}
                          />
                        </div>

                        {/* Name + details */}
                        <div className="ir-info">
                          <span className="ir-name">
                            {e.otherUser?.name || "Unknown"}
                          </span>
                          <span className="ir-sub">
                            {e.otherUser?.age ? `${e.otherUser.age} yrs` : ""}
                            {e.otherUser?.city ? ` · ${e.otherUser.city}` : ""}
                          </span>
                          <span className="ir-sub">
                            {e.otherUser?.religion || ""}
                            {e.otherUser?.caste ? ` / ${e.otherUser.caste}` : ""}
                          </span>
                        </div>

                        {/* Date + status badge */}
                        <div className="ir-meta">
                          <span className="ir-date">
                            {e.createdAt
                              ? new Date(e.createdAt).toLocaleDateString(
                                  undefined,
                                  { day: "numeric", month: "short", year: "numeric" }
                                )
                              : "—"}
                          </span>
                          <span className={`ir-status ir-status--${e.status || "pending"}`}>
                            {STATUS_LABEL[e.status || "pending"]}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="ir-actions">
                          <button
                            className="ir-btn ir-btn--view"
                            title="View Profile"
                            onClick={() => handleViewClick(e.interestId, profileId)}
                          >
                            <i className="ti ti-eye" /> View
                          </button>
                          {activeTab === "received" && isPending && (
                            <>
                              <button
                                className="ir-btn ir-btn--accept"
                                disabled={actionPending[e.interestId]}
                                onClick={() => handleAction(e.interestId, "accepted")}
                              >
                                <i className="ti ti-check" /> Accept
                              </button>
                              <button
                                className="ir-btn ir-btn--decline"
                                disabled={actionPending[e.interestId]}
                                onClick={() => handleAction(e.interestId, "declined")}
                              >
                                <i className="ti ti-close" /> Decline
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </section>
        </section>

        <Footer />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default Intrest;
